
// Revisado: 2026-01-03 [I18n Scan]
import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { API_BASE_URL } from '../../../../constants';
import { useAuth } from '../../../../context/AuthContext';

const DeckEventSummary = ({ slots }) => {
    const { t } = useTranslation();
    const { token } = useAuth();
    const [eventsData, setEventsData] = useState({});
    const [skillData, setSkillData] = useState({});
    const [loading, setLoading] = useState(false);

    // 1. Extract IDs from slots
    const activeIds = useMemo(() => {
        if (!slots) return [];
        return slots
            .filter(s => s && s.support)
            .map(s => s.support.support_id || s.support.id)
            // Deduplicate
            .filter((value, index, self) => self.indexOf(value) === index);
    }, [slots]);

    // 2. Fetch Events when IDs change
    useEffect(() => {
        if (activeIds.length === 0) {
            setEventsData({});
            return;
        }

        const fetchEvents = async () => {
            setLoading(true);
            try {
                const res = await fetch(`${API_BASE_URL}/api/supports/events/batch`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({ support_ids: activeIds })
                });

                if (res.ok) {
                    const data = await res.json();
                    setEventsData(data.events || {});
                    setSkillData(data.skill_data || {});
                }
            } catch (err) {
                console.error("Failed to fetch batch events", err);
            } finally {
                setLoading(false);
            }
        };

        // Debounce slightly to avoid rapid calls while building deck
        const timer = setTimeout(fetchEvents, 500);
        return () => clearTimeout(timer);

    }, [activeIds, token]);

    if (activeIds.length === 0) return null;

    // Helper to render effect
    const renderEffect = (eff) => {
        let icon = null;
        let text = eff.text || JSON.stringify(eff);
        // Fallback for badly parsed JSON, sometimes effect is just a string or object with key/value
        if (eff.value) text = eff.value;

        let colorClass = "text-slate-600 dark:text-slate-300";

        // Logic (similar to SupportEvents)
        if (eff.skill_id && skillData[eff.skill_id]) {
            const skill = skillData[eff.skill_id];
            const iconPath = `/images/Icon/Skill_Icons/icon_skill_${skill.icon_id}.png`;
            icon = <img src={`${API_BASE_URL}/api/thumb?img=${iconPath}&width=64`} className="w-5 h-5 object-contain" alt="skill" />;
            text = skill.name_en || skill.name_jp;
            if (eff.bonus > 0) {
                text += t('DeckEventSummary.level_bonus', { bonus: eff.bonus });
            }
            colorClass = "text-purple-600 dark:text-purple-400 font-bold";
        } else if (eff.type === 'stat') {
            colorClass = "text-green-600 dark:text-green-400 font-bold";
        } else if (eff.type === 'mood') {
            colorClass = "text-orange-500 font-medium";
        }

        // Just text parsing fallback if needed
        if (typeof eff === 'string') text = eff;

        // Remove quotes if JSON stringified accidentally
        if (typeof text === 'string' && text.startsWith('"') && text.endsWith('"')) {
            text = text.slice(1, -1);
        }

        return (
            <div className={`flex items-center gap-2 text-xs ${colorClass}`}>
                {icon}
                <span className="truncate">{text}</span>
            </div>
        );
    };

    return (
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden mt-6 mb-8 animate-in fade-in duration-500">
            <div className="bg-slate-100 dark:bg-slate-900 p-4 border-b border-slate-200 dark:border-slate-700 flex justify-between items-center">
                <div className="flex items-center gap-2">
                    <div className="p-1.5 bg-purple-500 text-white rounded-lg shadow-sm">
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                        </svg>
                    </div>
                    <h3 className="font-bold text-slate-700 dark:text-slate-200 uppercase text-sm tracking-wider">
                        {t('DeckEventSummary.title')}
                    </h3>
                </div>
                {loading && (
                    <span className="text-xs text-slate-400 animate-pulse font-mono">{t('DeckEventSummary.scanning')}</span>
                )}
            </div>

            <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-4 bg-slate-50 dark:bg-slate-900/50 max-h-[800px] overflow-y-auto custom-scrollbar">
                {activeIds.map(id => {
                    // Find card info from slots specifically for the header
                    // We need to look up active slots
                    const slot = slots.find(s => s && s.support && (Number(s.support.support_id) === Number(id) || Number(s.support.id) === Number(id)));
                    if (!slot) return null;
                    const card = slot.support;
                    const events = eventsData[id] || [];

                    if (events.length === 0) return null;

                    return (
                        <div key={id} className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm flex flex-col overflow-hidden hover:shadow-md transition-shadow">
                            {/* Card Header */}
                            <div className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-800 border-b border-slate-100 dark:border-slate-700">
                                <img
                                    src={`${API_BASE_URL}/api/thumb?img=/images/Icon/Support_Character_Icon/Support_Card_${id}_Icon.png&width=64`}
                                    className="w-10 h-10 rounded shadow-sm object-cover"
                                    alt=""
                                    loading="lazy"
                                />
                                <div className="min-w-0">
                                    <div className="text-xs font-bold text-slate-800 dark:text-slate-200 truncate">{card.char_name || card.name}</div>
                                    <div className="text-[10px] text-slate-400 font-medium">{t('DeckEventSummary.events_available_count', { count: events.length })}</div>
                                </div>
                            </div>

                            {/* Events List */}
                            <div className="p-3 space-y-4 overflow-y-auto max-h-96 custom-scrollbar bg-white dark:bg-slate-800/50">
                                {events.map((ev, idx) => (
                                    <div key={idx} className="text-sm">
                                        <div className="flex justify-between items-start mb-1 gap-2">
                                            <span className="font-bold text-[11px] text-slate-700 dark:text-slate-300 leading-tight">
                                                {ev.event_name}
                                            </span>
                                            {ev.event_type && (
                                                <span className="text-[9px] px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-700 text-slate-500 whitespace-nowrap">
                                                    {ev.event_type}
                                                </span>
                                            )}
                                        </div>

                                        {/* Options Compact */}
                                        <div className="border-l-2 border-slate-200 dark:border-slate-700 pl-2 ml-0.5 space-y-1.5">
                                            {ev.options.map((opt, oIdx) => (
                                                <div key={oIdx} className="group">
                                                    <div className="text-[10px] text-slate-400 font-medium group-hover:text-blue-500 transition-colors cursor-default">
                                                        {opt.option_name}
                                                    </div>
                                                    <div className="grid gap-0.5">
                                                        {opt.effects.map((eff, eIdx) => (
                                                            <div key={eIdx}>{renderEffect(eff)}</div>
                                                        ))}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default DeckEventSummary;
